import React, { FC, useEffect, useRef, useState } from 'react';
import { Select, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDownShort } from 'common/svg';
import { AuthHooks, UserHooks } from 'common/hooks';
import { useAppSelector } from 'app/store/store.hooks';
import { CatalogGoodsListFiltersProperty } from 'entities/Catalog/components';
import { IGoodPosition } from 'entities/Goods/Goods.models';
import { selectXBasketId } from 'entities/Auth/Auth.slice';
import { selectPropertyList } from 'entities/Property/Property.slice';
import { getPropertyPresetType } from 'entities/Property/Property.helper';
import { IPresetRange } from 'entities/Presets/Preset.models';
import { buildCatalogUrlParams } from 'entities/Catalog/Catalog.helper';

const CONTAINER_DEFAULT_HEIGHT = 88;

interface IComponentProps {
  categoryId?: number;
  isAvailableForCustomer?: boolean;
  isAvailableForPurchase?: boolean;
  positions?: IGoodPosition[];
  onAvailableForCustomerChange?: (value: boolean) => Promise<void>;
  onAvailableForPurchaseChange?: (value: boolean) => void;
  onClear?: () => void;
}

export const CatalogGoodsListFilters: FC<IComponentProps> = ({
  categoryId,
  isAvailableForCustomer,
  isAvailableForPurchase,
  positions = [],
  onAvailableForCustomerChange,
  onAvailableForPurchaseChange,
  onClear,
}) => {
  // auth
  const xBasketId = useAppSelector(selectXBasketId);
  // property
  const propertyList = useAppSelector(selectPropertyList);

  const [expanded, setExpanded] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSeller } = AuthHooks.useIsSeller();
  const { isSelfRegistered } = UserHooks.useIsSelfRegistered();

  const resizeContainer = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();

    if (expanded && containerRect.height === CONTAINER_DEFAULT_HEIGHT) {
      setExpanded(false);
      setCounter((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => resizeContainer());

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [expanded]);

  const handleExpand = () => {
    setExpanded(true);
    setCounter(!expanded ? counter : counter + 1);
  };

  const handleCollapse = () => {
    setExpanded(false);
    setCounter(!expanded ? counter : counter + 1);
  };

  useEffect(() => {
    handleCollapse();
  }, [categoryId]);

  const onApplyPositionClick = (propertyId: number, values: string[]): void => {
    const property = propertyList.find((p) => p.id === propertyId);
    // eslint-disable-next-line
    const { isRange } = getPropertyPresetType(property!);

    let newPositions: IGoodPosition[];

    if (isRange) {
      const from = values[0] ? Number(values[0]) : undefined;
      const to = values[1] ? Number(values[1]) : undefined;
      const hasValue = from !== undefined || to !== undefined;

      if (!hasValue) {
        newPositions = positions.filter((item) => item.propertyId !== propertyId);
      } else {
        const rangePosition = { propertyId, range: { from, to } } as IGoodPosition;
        const exists = positions.some((item) => item.propertyId === propertyId);

        newPositions = exists
          ? positions.map((item) => (item.propertyId === propertyId ? rangePosition : item))
          : [...positions, rangePosition];
      }
    } else {
      if (!values.length) {
        newPositions = positions.filter((item) => item.propertyId !== propertyId);
      } else {
        const exists = positions.some((item) => item.propertyId === propertyId);

        newPositions = exists
          ? positions.map((item) => (item.propertyId === propertyId ? { ...item, values } : item))
          : [...positions, { propertyId, values }];
      }
    }

    const params = buildCatalogUrlParams(newPositions);

    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
      replace: true,
      state: location.state,
    });
  };

  const handleClearClick = () => {
    navigate(location.pathname, {
      replace: true,
      state: location.state,
    });
    onClear?.();
  };

  return (
    <div key={counter} ref={containerRef}>
      <Typography.Paragraph
        className="mb-0 catalog-goods-list-filters"
        ellipsis={{
          rows: 4,
          expandable: true,
          onExpand: handleExpand,
          symbol: (
            <Typography.Text>
              {onClear && !!positions.length && (
                <Typography.Text
                  className="mr-4 inline-flex align-center gap-3 ff-semibold fs-xs color-red lineH-0 text-nowrap cursor-pointer"
                  key="clear-filters"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearClick();
                  }}
                >
                  {`\u2715 Сбросить фильтры (${positions.length})`}
                </Typography.Text>
              )}

              <Typography.Text
                key="expand"
                className="ff-semibold fs-xs color-dark-green lineH-0 text-nowrap cursor-pointer"
              >{`Показать все (${propertyList.length})`}</Typography.Text>
            </Typography.Text>
          ),
        }}
      >
        {(!isSeller || (isSeller && xBasketId)) && (
          <Typography.Text className="mb-3 mr-4 inline-flex align-center gap-4 lineH-0" key="availability">
            {!isSelfRegistered && (
              <Select
                rootClassName="redesign catalog-goods-list-filters__select"
                value={!!isAvailableForCustomer}
                onChange={onAvailableForCustomerChange}
                options={[
                  { label: 'Доступны для заказа', value: true },
                  { type: 'divider' },
                  { label: 'Весь ассортимент', value: false },
                ]}
                popupMatchSelectWidth={false}
                suffixIcon={<ArrowDownShort className="icon-arrow-down-short-bright-green" />}
              />
            )}

            {onAvailableForPurchaseChange && isAvailableForCustomer && (
              <Select
                rootClassName="redesign catalog-goods-list-filters__select"
                value={!!isAvailableForPurchase}
                onChange={onAvailableForPurchaseChange}
                options={[{ label: 'Товары в наличии', value: true }, { type: 'divider' }, { label: 'Все товары', value: false }]}
                popupMatchSelectWidth={false}
                suffixIcon={<ArrowDownShort className="icon-arrow-down-short-bright-green" />}
              />
            )}
          </Typography.Text>
        )}

        {!!categoryId &&
          propertyList.map((property) => {
            if (property.isCategory) {
              return null;
            }

            const position = positions.find((p) => p.propertyId === property.id);

            return (
              <Typography.Text className="mb-3 mr-4 inline-block lineH-0" key={property.id}>
                <CatalogGoodsListFiltersProperty
                  property={property}
                  currentValues={position?.values ?? []}
                  currentRange={position?.range as IPresetRange | undefined}
                  onApply={onApplyPositionClick}
                />
              </Typography.Text>
            );
          })}

        {onClear && !!positions.length && (
          <Typography.Text
            className="mr-4 inline-flex align-center gap-3 ff-semibold fs-xs color-red lineH-0 text-nowrap cursor-pointer"
            key="clear-filters"
            onClick={handleClearClick}
          >
            {`\u2715 Сбросить фильтры (${positions.length})`}
          </Typography.Text>
        )}

        {expanded && (
          <Typography.Text
            className="ff-semibold fs-xs color-dark-green lineH-0 text-nowrap cursor-pointer"
            key="collapse"
            onClick={handleCollapse}
          >
            Показать меньше
          </Typography.Text>
        )}
      </Typography.Paragraph>
    </div>
  );
};
